<template>
    <div class="reports">
        <v-card class="w600" outlined>
            <v-card-title class="page-heading primary minh60">
                <v-row align="center">
                    <v-col cols="12">{{ $t('reports.heading') }}</v-col>
                </v-row>
            </v-card-title>

            <v-card-text class="form-container">
                <reports
                    :admin="true"
                    :products="products"
                    :clients="clients"
                    :cash-registers="cashRegisters"
                    :shifts="shifts"
                    @create="onReportCreate"
                />
            </v-card-text>
        </v-card>

        <cod-dialog
            :show.sync="showReport"
            :max-width="report && report.width ? report.width : undefined"
        >
            <!-- Header -->
            <template v-if="report" slot="header">
                <span>{{ $t(`reports.${report.type}`) }}</span>

                <v-spacer />
                
                <v-btn
                    class="minw100"
                    text
                    dark
                    @click="togglePrint"
                >
                    <v-icon class="mr-3">fal fa-print</v-icon>

                    {{ $t('general.print') }}
                </v-btn>

                <v-menu
                    v-if="report.isSalesReport() || report.type === 'product_quantities'"
                    offset-y
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn v-on="on" icon>
                            <v-icon>far fa-ellipsis-v</v-icon>
                        </v-btn>
                    </template>

                    <v-list style="background-color:white">
                        <v-list-item
                            link
                            color="white"
                            outlined
                            class="minw100"
                            style="color:black"
                            @click="onExportToXlsx"
                        >
                            <v-icon class="mr-3">fal fa-file-excel</v-icon> {{ $t('general.export_xml') }}
                        </v-list-item>
                    </v-list>
                </v-menu>
            </template>

            <!-- Content -->
            <div slot="content">
                <v-slide-y-transition>
                    <div
                        v-if="initPrint && enableReceiptPrinter"
                        class="d-flex align-center flex-column"
                    >
                        <v-checkbox
                            v-model="useReceiptPrinter"
                            :label="$t('general.print_on_receipt_printer')"
                        />

                        <div>
                            <v-btn
                                class="mx-2"
                                color="primary"
                                outlined
                                @click="initPrint = false"
                            >
                                <v-icon class="mr-3">fal fa-times</v-icon>
                                {{ $t('general.cancel') }}
                            </v-btn>

                            <v-btn
                                class="mx-2"
                                color="primary"
                                depressed
                                @click="printReport(useReceiptPrinter)"
                            >
                                <v-icon class="mr-3">fal fa-check</v-icon>
                                {{ $t('general.print') }}
                            </v-btn>
                        </div>
                    </div>
                </v-slide-y-transition>

                <div class="report-container" v-html="reportHtml"></div>
            </div>
        </cod-dialog>
    </div>
</template>

<script>
import { loadData } from '@/views/shared/loadPageData';
import CodDialog from '@/components/CodDialog';
import Reports from '@/views/shared/components/Reports';
import ExcelGen from '@/utilities/ExcelGen';
import Client from '@/models/Client';
import Product from '@/models/Product';
import CashRegister from '@/models/CashRegister';
import Shift from '@/models/Shift';

export default {
    components: {
        CodDialog,
        Reports
    },

    created() {
        loadData('/admin/load/reports').then(data => this.onDataLoaded(data));

        this.useReceiptPrinter = this.enableReceiptPrinter
        this.receiptPrinter = this.$printService.getAssignedPrinter('receipt')
    },

    data() {
        return {
            products: [],
            clients: [],
            cashRegisters: [],
            shifts: [],

            report: null,
            reportHtml: null,
            showReport: false,

            initPrint: false,
            receiptPrinter: null,
            useReceiptPrinter: false
        };
    },

    computed: {
        enableReceiptPrinter() {
            return !!this.receiptPrinter && this.device.platform !== 'web'
        }
    },

    methods: {
        onDataLoaded(data) {
            this.products = data.products.map(p => new Product(p));
            this.clients = data.clients.map(c => new Client(c));
            this.cashRegisters = data.cashRegisters.map(cr => new CashRegister(cr));
            this.shifts = data.shifts.map(s => new Shift(s));
        },

        onReportCreate(data) {
            this.report = data.report
            this.reportHtml = this.report.getPrintable().toHtml('report').join('')
            this.showReport = true
        },

        togglePrint() {
            if(!this.enableReceiptPrinter) {
                this.printReport(false)
                return
            }

            if(this.device.platform === 'android') {
                this.printReport(true)
                return
            }

            this.initPrint = !this.initPrint
        },

        printReport(useReceiptPrinter) {
            this.initPrint = false

            this.$printService.printReport(this.report, {
                type: this.report.type,
                choosePrinter: !useReceiptPrinter
            })
        },

        onExportToXlsx() {
            // Do not include additional data in the exported file if sales report
            let reportData = {}

            if (this.report.isSalesReport()) {
                const filteredSales = this.report.data.sales.map((saleItem) => ({
                    ...saleItem,
                    items: saleItem.items.map(({ additional, ...rest }) => rest)
                }));

                reportData = {
                    ...this.report,
                    data: {
                        ...this.report.data,
                        sales: filteredSales
                    },
                    type: 'total_sales_report'
                };
            }
            if (this.report.type === 'product_quantities') {
                reportData = {
                    data: [
                        ...this.report.data.data[0].items,
                    ],
                    type: 'stock_report'
                };
            }
            
            ExcelGen.exportToXlsx(reportData)
        }
    },
};
</script>

<style lang="scss" scoped>
.reports {
    .w600 {
        max-width: 600px;
    }

    .page-heading {
        padding: 12px 16px;
        color: white;
        font-size: 16px;
        word-break: break-word;
    }
}

.form-container {
    min-height: 300px;
    margin-bottom: 80px;
}

.report-container {
    padding: 24px 16px;
    font-size: 16px;
}
</style>
